import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { S, Icon, StatusBadge, EmptyState, formatDate } from '../regureStyles';

// D-20: policies table headers were static; add client-side sort per
// column. Keys map header id → the field on the row shape produced by
// toDisplayPolicy (usePolicyFunctions). Only `policyNumber → id`
// matched the earlier map, so every other column click was a no-op.
// Retest 2026-08-04 confirmed: "Sort (except Policy #) not working".
const SORT_FIELDS = {
  policyNumber:  'id',
  client:        'client',
  product:       'product',
  status:        'status',
  annualPremium: 'premium',
  period:        'start',
  broker:        'broker',
  claims:        'claims',
};

const Table = ({ history, loading, paginated, formatCHF }) => {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (id) => {
    if (sortKey === id) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(id); setSortDir('asc'); }
  };
  const headers = [
    { id: 'policyNumber',  label: t('screens.policiesScreen.colPolicyNumber') },
    { id: 'client',        label: t('screens.policiesScreen.colClient') },
    { id: 'product',       label: t('screens.policiesScreen.colProduct') },
    { id: 'status',        label: t('screens.policiesScreen.colStatus') },
    { id: 'annualPremium', label: t('screens.policiesScreen.colAnnualPremium') },
    { id: 'period',        label: t('screens.policiesScreen.colPeriod') },
    { id: 'broker',        label: t('screens.policiesScreen.colBroker') },
    { id: 'claims',        label: t('screens.policiesScreen.colClaims') },
  ];
  const sortedRows = (() => {
    if (!sortKey || !SORT_FIELDS[sortKey]) return paginated;
    const field = SORT_FIELDS[sortKey];
    return [...paginated].sort((a, b) => {
      const va = a?.[field];
      const vb = b?.[field];
      const na = typeof va === 'number' ? va : (typeof va === 'string' && /\d{4}-\d{2}-\d{2}/.test(va) ? new Date(va).getTime() : String(va || '').toLowerCase());
      const nb = typeof vb === 'number' ? vb : (typeof vb === 'string' && /\d{4}-\d{2}-\d{2}/.test(vb) ? new Date(vb).getTime() : String(vb || '').toLowerCase());
      if (na < nb) return sortDir === 'asc' ? -1 : 1;
      if (na > nb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  })();
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 32px' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : paginated.length === 0 ? (
        <EmptyState icon='file-check' title={t('screens.policiesScreen.emptyTitle')} description={t('screens.policiesScreen.emptyDescription')} />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {headers.map(h => (
                <th
                  key={h.id}
                  onClick={() => toggleSort(h.id)}
                  style={{
                    backgroundColor: 'rgb(248, 250, 252)',
                    padding: '13px 14px',
                    textAlign: 'left',
                    fontSize: 11,
                    fontWeight: 700,
                    color: S.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${S.borderLight}`,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  {h.label}
                  {sortKey === h.id && (
                    <span style={{ marginLeft: 6, color: S.burgundy }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((p, i) => (
              <tr
                key={p.id}
                onClick={() => history.push(`/policies/${p._id}`)}
                style={{ borderBottom: `1px solid ${S.borderLight}`, cursor: 'pointer', background: i % 2 === 0 ? '#fff' : S.bgCard, transition: 'background 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.background = S.bgHover)}
                onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard)}
              >
                <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 700, color: S.burgundy, whiteSpace: 'nowrap' }}>{p.id}</td>
                <td style={{ padding: '13px 14px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{p.client}</div>
                  <div style={{ fontSize: 12, color: S.textMuted }}>{p.canton}</div>
                </td>
                <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{p.product}</td>
                <td style={{ padding: '13px 14px' }}>
                  <StatusBadge status={p.status} />
                </td>
                <td style={{ padding: '13px 14px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{formatCHF(p.premium, p.currency)}</td>
                <td style={{ padding: '13px 14px', fontSize: 12, color: S.textMuted }}>
                  {formatDate(p.start)} – {formatDate(p.end)}
                </td>
                <td style={{ padding: '13px 14px', fontSize: 13, color: S.textSecondary }}>{p.broker}</td>
                <td style={{ padding: '13px 14px' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 13,
                      color: p.claims > 0 ? S.burgundy : S.textMuted,
                      fontWeight: p.claims > 0 ? 600 : 400,
                    }}
                  >
                    <Icon name='shield' color={p.claims > 0 ? S.burgundy : '#94a3b8'} size={13} /> {p.claims == null ? '—' : p.claims}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Table;
